import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import Lockr from "lockr";
import { useTranslation } from "react-i18next";
import FileDropzone from "../FileDropzone";
import {
	getFileExtension,
	getStorageFileName,
} from "src/utils/mediaFilesHelper";
import { HTTPSTATUSCODES, LogoFileExtensions } from "src/utils/constants";
import useAuth from "src/hooks/useAuth";
import { StorageKeys } from "src/utils/storageKeys";
import { webchatEntryPointService } from "src/services/webchatEntryPoints";
import {
	Backdrop,
	Box,
	CircularProgress,
	Grid,
	IconButton,
} from "@mui/material";
import { WebchatEntryPointLogoUploadProps } from "src/types/textEntryPoints";
import { mediaFiles } from "src/config";
import { useSnackbar } from "notistack";
import CloseIcon from "@mui/icons-material/Close";
import { useFormikContext } from "formik";

const WebchatEntryPointLogoUpload = ({
	fileName,
	onFileChange,
}: WebchatEntryPointLogoUploadProps) => {
	const { user } = useAuth();
	const { enqueueSnackbar } = useSnackbar();
	const [files, setFiles] = useState<File[]>([]);
	const previousUploadedFileName = useRef<string>("");
	const uploadedFileName = useRef<string>();
	const [fileChanged, setFileChanged] = useState(false);
	const maxMediaFileSize = useRef<number>(0.5); // 0.5 MB
	const formik = useFormikContext();
	const [openLoader, setOpenLoader] = useState(false);
	const { t } = useTranslation();

	const dropzoneContainerRef = useRef<HTMLDivElement>(null);
	const [dropzoneContainerHeight, setDropzoneContainerHeight] = useState(0);

	useLayoutEffect(() => {
		const dropzoneContainer = dropzoneContainerRef.current;
		if (!dropzoneContainer) {
			return undefined;
		}

		const update = () =>
			setDropzoneContainerHeight(
				dropzoneContainer.getBoundingClientRect().height
			);

		update();
		const resizeObserver = new ResizeObserver(update);
		resizeObserver.observe(dropzoneContainer);
		window.addEventListener("resize", update);

		return () => {
			resizeObserver.disconnect();
			window.removeEventListener("resize", update);
		};
	}, []);

	useEffect(() => {
		if (!formik.dirty) {
			setFiles([]);
		}
	}, [formik.dirty]);

	const handleDrop = (newFiles: File[]): void => {
		const allowedLogoFileExtensions = Object.values(LogoFileExtensions);
		const newFileExtension = getFileExtension(newFiles[0]?.name);
		if (!allowedLogoFileExtensions.includes(newFileExtension)) {
			enqueueSnackbar(
				t("TextEntryPoint.LogoUnsupportedMediaFileFormat"),
				{ variant: "error" }
			);
		} else if (newFiles[0]?.size / 1000000 > maxMediaFileSize.current) {
			enqueueSnackbar(
				t("TextEntryPoint.LogoMaxFileSize", {
					maxFileSize: maxMediaFileSize.current,
				}),
				{ variant: "error" }
			);
		} else {
			setFiles(newFiles);
		}
	};

	const onUpload = () => {
		const data = new FormData();
		const accountId = user.id || Lockr.get(StorageKeys.LocalAccountId);
		const fileToUploadName = getStorageFileName(files, accountId);
		if (uploadedFileName.current) {
			previousUploadedFileName.current = uploadedFileName.current;
		}

		data.append("file", files[0], fileToUploadName);
		uploadedFileName.current = fileToUploadName;
		handleUploadToStorage(data);
		setFileChanged(true);
	};

	const handleUploadToStorage = async (data: FormData): Promise<void> => {
		try {
			setOpenLoader(true);
			const response = await webchatEntryPointService.uploadLogoToStorage(
				data
			);
			if (response.status === HTTPSTATUSCODES.StatusCodeSuccess) {
				if (previousUploadedFileName.current) {
					deleteFileFromStorage(previousUploadedFileName);
				}

				setTimeout(() => {
					onFileChange(uploadedFileName.current);
					setOpenLoader(false);
				}, 1000);
			} else {
				uploadedFileName.current = "";
				setOpenLoader(false);
			}
		} catch (error) {
			console.log(error);
			setOpenLoader(false);
		}
	};

	const deleteFileFromStorage = async (
		fileNameToDelete: React.MutableRefObject<string>
	) => {
		try {
			const response =
				await webchatEntryPointService.deleteLogoFromStorage(
					fileNameToDelete.current
				);
			if (response.status === HTTPSTATUSCODES.StatusCodeSuccess) {
				fileNameToDelete.current = "";
				onFileChange(undefined);
			}
		} catch (error) {
			console.log(error);
		}
	};

	const removeUploadedFile = async () => {
		if (uploadedFileName.current) {
			deleteFileFromStorage(uploadedFileName);
		}

		setFiles([]);
	};

	const removeCurrentLogoFile = async () => {
		onFileChange("");
		setFiles([]);
	};

	const logoUrl = useMemo(() => {
		const tenantId = Lockr.get(StorageKeys.TenantId) ?? "";

		return `${mediaFiles.mediaBlobContainerUrl}/${tenantId}/webchat/logos/${fileName}`;
	}, [fileName]);

	return (
		<>
			<Backdrop
				sx={{
					color: "#fff",
					zIndex: (theme) => theme.zIndex.drawer + 1,
				}}
				open={openLoader}
			>
				<CircularProgress color="inherit" />
			</Backdrop>
			<Grid container spacing={2} alignItems="stretch">
				<Grid item xs={6} sx={{ minWidth: 0 }}>
					<Box ref={dropzoneContainerRef}>
						<FileDropzone
							accept={[
								".png",
								".jpg",
								".jpeg",
								".svg",
								"image/png",
								"image/jpeg",
								"image/svg+xml",
							]}
							files={files}
							maxFiles={1}
							onDrop={handleDrop}
							onRemove={removeUploadedFile}
							onUpload={onUpload}
						/>
					</Box>
				</Grid>
				<Grid item xs={6} sx={{ minWidth: 0, display: "flex" }}>
					{fileName && (
						<Box
							sx={{
								border: 1,
								borderRadius: 1,
								borderColor: "divider",
								flex: 1,
								height: dropzoneContainerHeight
									? `${dropzoneContainerHeight}px`
									: "auto",
								minHeight: 0,
								position: "relative",
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								overflow: "visible",
								boxSizing: "border-box",
							}}
						>
							<Box
								sx={{
									width: "100%",
									height: "100%",
									boxSizing: "border-box",
									p: 1,
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
								}}
							>
								<img
									src={logoUrl}
									alt="logo"
									style={{
										maxWidth: "100%",
										maxHeight: "100%",
										width: "auto",
										height: "auto",
										objectFit: "contain",
										objectPosition: "center",
										display: "block",
									}}
								/>
							</Box>
							{!fileChanged && (
								<IconButton
									title={t(
										"TextEntryPoint.LogoUnsupportedMediaFileFormat"
									)}
									onClick={() => removeCurrentLogoFile()}
									style={{
										alignSelf: "flex-start",
										flexShrink: 1,
										margin: "4px",
									}}
								>
									<CloseIcon fontSize="small" />
								</IconButton>
							)}
						</Box>
					)}
				</Grid>
			</Grid>
		</>
	);
};

export default WebchatEntryPointLogoUpload;
